/*
//-------------------------------------------------------
//   Copyright (c) 2024 Templa Computer Systems Ltd
//
//   App:        EmployeePortal.UI
//   Object:     DataFormField
//   DataForms:    DataForm field for DataForm
//
// Log      Date   By  Scan Description
// -----  -------- --- ---- ------------------------------
// WI3713 26/01/24 JC		New
//-------------------------------------------------------
*/
import React, { Component } from 'react';
import { connect } from 'react-redux';
import DateTime from './DateTime'; 
import { dateTimeTypes } from '../constants/DateTimeTypes';
import { dataFormDataTypes } from '../constants/DataFormDataTypes'
import './css/DataFormField.css';
import { utils } from '../utils/utils'; 

class DataFormField extends Component {
	constructor(props) {
		super(props);

		const decimalPlaces = this.getDecimalPlaces(props.item.dataType);
		const inputType = this.getInputType(props.item.dataType);
        const answer = utils.formatValue(props.item.answer, inputType, decimalPlaces); 
        const originalAnswer = utils.formatValue(props.item.originalAnswer, inputType, decimalPlaces); 
		this.state = {
			inputType,
			decimalPlaces,
            originalAnswer: originalAnswer,
			answer: answer,
			isPending: props.item.isPending,
            isChanged: false,
            propsediting: false
		};

		this.handleSelect = this.handleSelect.bind(this);
		this.handleChecked = this.handleChecked.bind(this);
		this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleDatetimeChange = this.handleDatetimeChange.bind(this);
        this.handleDatetimeBlur = this.handleDatetimeBlur.bind(this);
	}

    static getDerivedStateFromProps(props, state) {
        let item = props.item;
        let answer = utils.formatValue(item.answer, state.inputType, state.decimalPlaces);
        let originalAnswer = utils.formatValue(item.originalAnswer, state.inputType, state.decimalPlaces);

        //only if props editing changed
        if (props.editing !== state.propsediting ) {

            //return new state
            return {
                answer: answer,
                originalAnswer: originalAnswer, 
                isPending: item.isPending,
                isChanged: false,
                propsediting: props.editing
            };
        }

        // Return null to indicate no change to state.
        return null;
    }

    getInputType(dataType) {
        //this is based on DetailField, and for the purposes of reusing that logic we're just going to convert DataFormDataTypes to text
        switch (dataType) {
            case dataFormDataTypes.none:
            case dataFormDataTypes.text:
                return "text";
                break;
            case dataFormDataTypes.check_box:
                return "checkbox";
                break;
            case dataFormDataTypes.date_and_time:
                return "datetime";
                break;
            case dataFormDataTypes.list:
                return "select";
                break;
            case dataFormDataTypes.numeric_0dp:
            case dataFormDataTypes.numeric_1dp:
            case dataFormDataTypes.numeric_2dp:
            case dataFormDataTypes.numeric_3dp:
            case dataFormDataTypes.numeric_4dp:
                return "number";
                break;
            case dataFormDataTypes.multiline_text: 
                return "textarea";
                break;
            case dataFormDataTypes.url:
                return "text";
                break;
            default:
                //should not happen
                return "text";
                break;
        }
	}

	getDecimalPlaces(dataType) {
        switch (dataType) {            
            case dataFormDataTypes.numeric_0dp:
                return 0;
                break;
            case dataFormDataTypes.numeric_1dp:
                return 1;
                break;
            case dataFormDataTypes.numeric_2dp:
                return 2;
                break;
            case dataFormDataTypes.numeric_3dp:
                return 3;
                break;
            case dataFormDataTypes.numeric_4dp:
                return 4;
                break;            
            default:
                return null;
                break;
        }
	}

	handleSelect = event => {
		var newAnswer = event.target.value;
		var isChanged = newAnswer !== this.state.originalAnswer;
		this.setState({
			answer: newAnswer,
			isChanged: isChanged
		});
        this.props.changed({ lineNumber: this.props.item.lineNumber, isChanged, answer: newAnswer, dataType: this.props.item.dataType});
	}

	handleChecked = event => {
		var newAnswer = event.target.checked;
		var isChanged = newAnswer !== this.state.originalAnswer;
		this.setState({
			answer: newAnswer,
			isChanged: isChanged
		});
		this.props.changed({
            lineNumber: this.props.item.lineNumber, isChanged, answer: newAnswer.toString(), // convert new answer to string
			dataType: this.props.item.dataType
		}); //M01
	}

	handleChange = event => {
		this.setState({
            answer: event.target.value
		});
	}

	handleBlur = event => {
        event.preventDefault();
        //replace \n (with or without \r at start with \r\n so we don't get dirty flags being set unnecessarily on text areas
        var newAnswer = utils.formatValue(event.target.value, this.state.inputType, this.state.decimalPlaces).replace(/\r?\n/g, "\r\n");
		var isChanged = newAnswer !== this.state.originalAnswer;
		this.setState({
			answer: newAnswer,
            isChanged: isChanged
		});
        this.props.changed({ lineNumber: this.props.item.lineNumber, isChanged, answer: newAnswer, dataType: this.props.item.dataType}); //M01
    }

    handleDatetimeChange = momentDate => {
        this.setState({
            answer: momentDate
        });
    }

    handleDatetimeBlur = momentDate => {
        var newAnswer = utils.formatValue(momentDate, this.state.inputType, this.state.decimalPlaces);
        var isChanged = newAnswer !== this.state.originalAnswer;
        this.setState({
            answer: newAnswer,
            isChanged: isChanged
        });
        this.props.changed({ lineNumber: this.props.item.lineNumber, isChanged, answer: newAnswer, dataType: this.props.item.dataType}); //M01
    }

    render() {
		const { item } = this.props;
		const dataFormChangedClass = this.state.isChanged ? "dataform-changed" : "";
        const dataFormPendingClass = this.props.item.isPending ? "dataform-pending" : "";
        const dataFormErrorClass = this.props.item.hasError ? "dataform-error" : "";

        //build the url text - prefix with htpp if necessary
        let sURL = "";
        if (item.dataType === dataFormDataTypes.url && item.defaultValue.length > 0) {
            sURL = item.defaultValue;
            if (!sURL.toLowerCase().startsWith("http")) {
                sURL = "http://" + sURL;
            }
        }

		if (!this.props.editing ) {
            var field;
            
            if (this.state.inputType === "date" || this.state.inputType === "datetime") {

                //get the correct date time type
                let dateTimeType = dateTimeTypes.dateTime;
                if (this.state.inputType === "date") { dateTimeType = dateTimeTypes.date }

                field = <DateTime
                    componentKey={item.lineNumber}
                    componentName={item.lineNumber}
                    value={this.state.answer}
                    dateTimeType={dateTimeType}
                    dateIsUtc={true}
                    disabled={true}
                />

            }
			else if (this.state.inputType === "checkbox") {
				field = this.state.answer ? "Yes" : "No";
			}
			else {
				field = <span>{this.state.answer}</span>;
			}

			return (
                <div className={`dataform-field ${dataFormChangedClass} ${dataFormPendingClass} ${dataFormErrorClass}`}>{field}</div>
			);
		}
		
        if (this.state.inputType === "select") {
            return (
                <select
                    key={`select~${item.lineNumber}`}
                    name={item.lineNumber}
                    onChange={this.handleSelect}
                    value={this.state.answer}
                    className={`dataform-field ${dataFormChangedClass} ${dataFormPendingClass} ${dataFormErrorClass}`}
                >
                    {!item.hasAnswer ? <option key={"selectitem0"}></option> : [] /* add a blank option if there's no answer */}
                    {item.itemValues.map((opt, i) => <option key={`selectitem~${opt.lineNumber}`}>{opt.itemValue}</option>)}
                </select>
            );
        }
        else if (this.state.inputType === "checkbox") {
            return (
                <input
                    type="checkbox"
                    key={item.lineNumber}
                    name={item.lineNumber}
                    onChange={this.handleChecked}
                    value="true"
                    checked={this.state.answer}
                    className={`dataform-field ${dataFormChangedClass} ${dataFormPendingClass} ${dataFormErrorClass}`}
                />
            );
        }
        /* M04 date /datetime handling - use datetime component */
        else if (this.state.inputType === "date" || this.state.inputType === "datetime") {

            //get the correct date time type
            let dateTimeType = dateTimeTypes.dateTime;
            if (this.state.inputType === "date") { dateTimeType = dateTimeTypes.date }

            return (<DateTime
                componentKey={item.lineNumber}
                componentName={item.lineNumber}
                className={`dataform-field ${dataFormChangedClass} ${dataFormPendingClass} ${dataFormErrorClass}`}
                value={this.state.answer}
                onBlur={this.handleDatetimeBlur}
                onChange={this.handleDatetimeChange}
                dateTimeType={dateTimeType}
                dateIsUtc={true}
                disabled={false}
            />
            );
        }
        /* textarea */
        else if (this.state.inputType === "textarea") {
            return (<textarea
                key={item.lineNumber}
                name={item.lineNumber}
                value={this.state.answer}
                onBlur={this.handleBlur}
                onChange={this.handleChange}
                className={`dataform-textarea ${dataFormChangedClass} ${dataFormPendingClass} ${dataFormErrorClass}`}
            />
            );
        }
        /* handle url - blank attribute opens in new window/tab */
        else if (sURL.length > 0) {
            return <a href={sURL} target="_blank">{this.state.answer}</a>;
        }

		return (
			<div>
				<input
                    type={this.state.inputType}
                    key={item.lineNumber}
                    name={item.lineNumber}
                    value={this.state.answer}
                    onBlur={this.handleBlur}
                    onChange={this.handleChange}
                    className={`dataform-field ${dataFormChangedClass} ${dataFormPendingClass} ${dataFormErrorClass}`}
				/>
			</div>
		);
	}
}

export default connect()(DataFormField);